/* eslint-disable react/jsx-props-no-spreading */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { injectIntl, FormattedMessage } from 'react-intl';

import Dropdown from '../form/Dropdown';
import Input from '../form/Input';
import Checkbox from '../form/Checkbox';
import RadioButton from '../form/RadioButton';
import BottomBar from '../BottomBar';
import { yesNo } from '../helpers';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { TIMEZONES } from '../../../utils/timezone';
import normalizePhone from '../../../utils/normalizePhone';
import { COUNTRY } from '../../../environment';
import {
  updatePreferences,
  updateProviderDetails,
  updatePhoneNumbers,
  updateAddress,
} from '../../../actions/preferences';

const LocationDetails = ({
  specialized,
  intl,
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  previousPage,
  errorCallback,
  draftValues,
  onDraftChange,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = intl;
  const states = useSelector(state =>
    _.map(_.get(state, 'misc.states', []), s => ({
      label: s.label,
      value: s.code,
    })),
  );

  const user = useSelector(state => state.user);

  const initialDefaults = {
    street: user?.practice_address?.street || '',
    alternate_street: user?.practice_address?.alternate_street || '',
    city: user?.practice_address?.city || '',
    country:
      COUNTRY === 'US'
        ? {
            value: 'USA',
            label: formatMessage({ defaultMessage: 'United States' }),
          }
        : { value: 'CAN', label: formatMessage({ defaultMessage: 'Canada' }) },
    code: user?.practice_address?.code || null,
    timezone: user?.preference?.timezone
      ? { value: user.preference.timezone, label: user.preference.timezone }
      : null,
    sms_number: user?.sms_number?.number || '',
    sms_reminder: user?.preference?.sms_reminder_yn || false,
  };

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialDefaults,
      state_code: null,
      // JavaScript objects cannot have duplicate keys.
      // so draftValues will override if same key exists in initialDefaults.
      ...(draftValues || {}),
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  // When user or states change, update state_code if needed
  useEffect(() => {
    if (states.length > 0 && user?.practice_address?.state?.code) {
      const defaultState = states.find(
        s => s.value === user.practice_address.state.code,
      );
      if (defaultState) {
        setValue('state_code', defaultState);
      }
    }
  }, [states, user, setValue]);

  const onSubmit = async values => {
    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem updating your location details." />
    );

    const preferenceValues = {
      preference: {
        timezone: _.get(values, 'timezone.value'),
        sms_reminder_yn: !!values.sms_reminder,
      },
    };
    const providerDetailValues = {
      provider_detail: { custom_tax_yn: values.custom_tax_yn },
    };
    const phoneNumberValues = {
      mobile_phone: values.sms_number,
    };
    const addressValues = {
      address: {
        street: values.street,
        alternate_street: values.alternate_street,
        city: values.city,
        code: values.code,
        state_code: _.get(values, 'state_code.value'),
      },
    };

    try {
      openLoadingPanel(
        formatMessage({ defaultMessage: 'Updating location details' }),
      );
      const promises = [
        dispatch(updatePreferences(preferenceValues)),
        dispatch(updatePhoneNumbers(phoneNumberValues)),
        dispatch(updateAddress(addressValues)),
      ];

      if (COUNTRY === 'US') {
        promises.push(dispatch(updateProviderDetails(providerDetailValues)));
      }

      await Promise.all(promises);
      closeLoadingPanel();
      nextPage();
    } catch (error) {
      errorCallback(error, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title" style={{ marginBottom: '20px' }}>
        <h1>
          <FormattedMessage defaultMessage="Location Details" />
        </h1>
      </div>
      <h4 style={{ marginBottom: '40px' }}>
        <FormattedMessage
          defaultMessage="Only your {provinceOrState} and country will be visible to your clients, unless you indicate that you provide in-person sessions."
          values={{ provinceOrState: COUNTRY === 'US' ? 'state' : 'province' }}
        />
      </h4>

      {/* Address */}
      <div className="input-row">
        <Controller
          name="street"
          control={control}
          rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
          render={({ field }) => (
            <Input
              {...field}
              required
              label={formatMessage({ defaultMessage: 'Street 1' })}
              error={errors.street?.message}
            />
          )}
        />
        <Controller
          name="alternate_street"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              label={formatMessage({ defaultMessage: 'Street 2' })}
              placeholder={formatMessage({
                defaultMessage: 'Unit, Suite, Apt number',
              })}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
          render={({ field }) => (
            <Input
              {...field}
              required
              label={formatMessage({ defaultMessage: 'City/Town' })}
              error={errors.city?.message}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="country"
          control={control}
          rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
          render={({ field }) => (
            <Dropdown
              {...field}
              required
              disabled
              label={formatMessage({ defaultMessage: 'Country' })}
              options={
                COUNTRY === 'US'
                  ? [
                      {
                        value: 'USA',
                        label: formatMessage({
                          defaultMessage: 'United States',
                        }),
                      },
                    ]
                  : [
                      {
                        value: 'CAN',
                        label: formatMessage({ defaultMessage: 'Canada' }),
                      },
                    ]
              }
            />
          )}
        />
        <Controller
          name="state_code"
          control={control}
          rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
          render={({ field }) => (
            <Dropdown
              {...field}
              required
              label={
                COUNTRY === 'US'
                  ? formatMessage({ defaultMessage: 'State' })
                  : formatMessage({ defaultMessage: 'Province/Territory' })
              }
              error={errors.state_code?.message}
              options={states}
            />
          )}
        />
        <Controller
          name="code"
          control={control}
          rules={{
            required: formatMessage({ defaultMessage: 'Required' }),
            validate: value => {
              if (!value) return formatMessage({ defaultMessage: 'Required' });

              if (COUNTRY === 'US') {
                return /^[0-9]{5}(?:-[0-9]{4})?$/.test(value)
                  ? true
                  : formatMessage({ defaultMessage: 'Invalid Zip code' });
              }

              if (COUNTRY === 'CA') {
                return /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]( )?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i.test(
                  value,
                )
                  ? true
                  : formatMessage({ defaultMessage: 'Invalid Postal code' });
              }

              return true;
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              required
              label={
                COUNTRY === 'US'
                  ? formatMessage({ defaultMessage: 'Zip Code' })
                  : formatMessage({ defaultMessage: 'Postal Code' })
              }
              error={errors.code?.message}
            />
          )}
        />
      </div>

      {/* Timezone */}
      <div className="input-row">
        <Controller
          name="timezone"
          control={control}
          rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
          render={({ field }) => (
            <Dropdown
              {...field}
              required
              label={formatMessage({ defaultMessage: 'Time Zone' })}
              options={TIMEZONES}
              error={errors.timezone?.message}
            />
          )}
        />
      </div>

      <div className="input-row">
        <Controller
          name="sms_number"
          control={control}
          rules={{
            required: formatMessage({ defaultMessage: 'Required' }),
            pattern: {
              value: /^\d{3}-\d{3}-\d{4}$/,
              message: formatMessage({
                defaultMessage: 'Invalid phone number',
              }),
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              required
              label={formatMessage({ defaultMessage: 'Mobile Phone Number' })}
              error={errors.sms_number?.message}
              onChange={e => {
                const formatted = normalizePhone(e.target.value);
                field.onChange(formatted); // update form value with normalized phone
              }}
            />
          )}
        />

        <Controller
          name="sms_reminder"
          control={control}
          defaultValue={false} // store boolean
          render={({ field }) => (
            <Checkbox
              options={[
                {
                  value: true,
                  label: (
                    <FormattedMessage defaultMessage="Ok to send SMS reminders to mobile phone number" />
                  ),
                },
              ]}
              onChange={val => field.onChange(val.includes(true))} // map array -> boolean
            />
          )}
        />
      </div>

      {/* US only */}
      {COUNTRY === 'US' && (
        <div className="input-row">
          <Controller
            name="custom_tax_yn"
            control={control}
            rules={{ required: formatMessage({ defaultMessage: 'Required' }) }}
            render={({ field }) => (
              <RadioButton
                {...field}
                label={formatMessage({
                  defaultMessage: 'Are your clinical services tax exempt?',
                })}
                options={yesNo}
                required
              />
            )}
          />
        </div>
      )}

      <BottomBar
        specialized={specialized}
        currentStep={1}
        nextPage={nextPage}
        previousPage={previousPage}
      />
    </form>
  );
};

LocationDetails.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  states: PropTypes.array.isRequired,
  specialized: PropTypes.bool,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default injectIntl(LocationDetails);
